import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiFolder, FiPlus, FiSearch, FiX, FiCalendar, FiUsers, FiCheck } from 'react-icons/fi';

export default function Projects() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', deadline: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [nameCheckTimer, setNameCheckTimer] = useState(null);
  const [filter, setFilter] = useState('all');

  const loadProjects = (q, f) => {
    const params = new URLSearchParams();
    if (q) params.set('search', q);
    if (f === 'mine') params.set('filter', 'mine');
    else if (f === 'completed') params.set('filter', 'completed');
    const url = `/api/projects${params.toString() ? '?' + params.toString() : ''}`;
    authFetch(url).then(r => r.json()).then(data => { setProjects(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { loadProjects('', filter); }, [filter]);

  useEffect(() => {
    // Check if user has create permission
    if (user?.role === 'admin' || user?.permissions?.includes('admin_panel')) { setCanCreate(true); return; }
    authFetch(`/api/users/${user?.id}/profile`).then(r => r.ok ? r.json() : null).then(data => {
      if (data?.kpts?.some(k => (k.permissions || '').split(',').some(p => ['create_projects', 'manage_projects'].includes(p)))) setCanCreate(true);
    }).catch(() => {});
  }, []);

  const toTitleCase = (str) => {
    return str.replace(/\S+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  };

  const toSentenceCase = (str) => {
    if (!str) return str;
    let result = str.charAt(0).toUpperCase() + str.slice(1);
    result = result.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
    return result;
  };

  const checkNameExists = (name) => {
    if (nameCheckTimer) clearTimeout(nameCheckTimer);
    if (!name.trim()) { setNameExists(false); return; }
    const timer = setTimeout(() => {
      authFetch(`/api/projects/check-name?name=${encodeURIComponent(name.trim())}`)
        .then(r => r.json())
        .then(data => setNameExists(data.exists))
        .catch(() => {});
    }, 400);
    setNameCheckTimer(timer);
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const isDeadlineValid = !createForm.deadline || createForm.deadline >= todayStr;
  const isFormValid = createForm.name.trim() && !nameExists && isDeadlineValid;

  const handleSearch = (v) => {
    setSearch(v);
    loadProjects(v, filter);
  };

  const handleFilter = (f) => {
    setFilter(f);
    loadProjects(search, f);
  };

  // Client-side filter for not_started vs in_progress (both are Active on server)
  const filteredProjects = projects.filter(p => {
    if (filter === 'not_started') return p.status !== 'Complete' && p.completed_items === 0;
    if (filter === 'in_progress') return p.status !== 'Complete' && p.completed_items > 0 && p.completed_items < p.total_items;
    return true;
  });

  const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'Your Projects' },
    { key: 'not_started', label: 'Not Started' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
  ];

  const handleCreate = async () => {
    if (!createForm.name.trim()) { setError('Name is required'); return; }
    setCreating(true); setError('');
    try {
      const res = await authFetch('/api/projects', { method: 'POST', body: JSON.stringify(createForm) });
      if (res.ok) {
        const data = await res.json();
        setShowCreate(false);
        setCreateForm({ name: '', description: '', deadline: '' });
        navigate(`/projects/${data.id}`);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to create project');
      }
    } catch (e) { setError('Failed to create project'); }
    setCreating(false);
  };

  if (loading) return (
    <div>
      <div className="skeleton" style={{ width: 200, height: 28, marginBottom: 16 }} />
      {[...Array(4)].map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 80, borderRadius: 12, marginBottom: 10 }} />
      ))}
    </div>
  );

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>Projects</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}</p>
        </div>
        {canCreate && (
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreate(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            <FiPlus size={14} /> New Project
          </motion.button>
        )}
      </motion.div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => handleSearch(e.target.value)}
          placeholder="Search projects..."
          style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        {search && (
          <button onClick={() => handleSearch('')}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <FiX size={14} />
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTER_TABS.map(tab => (
          <button key={tab.key} onClick={() => handleFilter(tab.key)}
            style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid',
              borderColor: filter === tab.key ? 'var(--accent)' : 'var(--border)',
              background: filter === tab.key ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)',
              color: filter === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.2s'
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Project List */}
      {filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <FiFolder size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>No projects found</div>
          {canCreate && <div style={{ fontSize: 13, marginTop: 4 }}>Create your first project to get started</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredProjects.map(p => {
            const pct = p.total_items > 0 ? Math.round((p.completed_items / p.total_items) * 100) : 0;
            const isComplete = p.status === 'Complete';
            return (
              <motion.div key={p.id} whileHover={{ y: -2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                onClick={() => navigate(`/projects/${p.id}`)}
                style={{
                  padding: '16px 18px', borderRadius: 12, border: '1px solid var(--border)',
                  background: 'var(--bg-card)', cursor: 'pointer', transition: 'all 0.2s'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: isComplete ? 'rgba(16,185,129,0.1)' : 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {isComplete ? <FiCheck size={16} color="#10b981" /> : <FiFolder size={16} color="#2563eb" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {p.tag && <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: '#2563eb', background: 'rgba(37,99,235,0.08)', padding: '1px 6px', borderRadius: 4 }}>{p.tag}</span>}
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{p.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                        background: isComplete ? 'rgba(16,185,129,0.1)' : 'rgba(37,99,235,0.08)',
                        color: isComplete ? '#10b981' : '#2563eb'
                      }}>{p.status}</span>
                    </div>
                    {p.description && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: pct === 100 ? '#10b981' : pct > 0 ? '#2563eb' : 'var(--text-muted)' }}>{pct}%</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.completed_items}/{p.total_items} items</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 2, background: pct === 100 ? '#10b981' : '#2563eb', width: `${pct}%`, transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  {p.member_count > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiUsers size={11} /> {p.member_count}</span>}
                  {p.deadline && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiCalendar size={11} /> {new Date(p.deadline + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                  {p.created_by_name && <span>by {p.created_by_name}</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Project Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>New Project</h3>
                <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Project Name *</label>
                  <input value={createForm.name} onChange={e => {
                    const val = toTitleCase(e.target.value);
                    setCreateForm(f => ({ ...f, name: val }));
                    checkNameExists(val);
                  }} autoFocus
                    placeholder="Project name..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${nameExists ? '#ef4444' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  {nameExists && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, fontWeight: 500 }}>A project with this name already exists</div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                  <textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: toSentenceCase(e.target.value) }))}
                    placeholder="Project description..."
                    rows={3}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Deadline</label>
                  <input type="date" value={createForm.deadline} min={todayStr}
                    onChange={e => setCreateForm(f => ({ ...f, deadline: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${createForm.deadline && !isDeadlineValid ? '#ef4444' : 'var(--border)'}`, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  {createForm.deadline && !isDeadlineValid && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, fontWeight: 500 }}>Deadline cannot be before today</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setShowCreate(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <motion.button whileHover={!creating && isFormValid ? { scale: 1.02 } : {}} whileTap={!creating && isFormValid ? { scale: 0.98 } : {}}
                  onClick={handleCreate} disabled={creating || !isFormValid}
                  style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: creating || !isFormValid ? '#94a3b8' : '#2563eb', color: '#fff', cursor: creating || !isFormValid ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: creating || !isFormValid ? 0.6 : 1 }}>
                  {creating ? 'Creating...' : 'Create Project'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
